import React from 'react';
import { DriverTour } from './outbox';

/**
 * Skica dana: kuda tura ide i kojim redom.
 *
 * Crta se kao SVG, iz koordinata koje tura već nosi — bez ijedne slike sa
 * interneta. To je jedini način da mapa postoji i na putu, gde signala nema:
 * prava mapa bi tražila pločice, a njih telefon u Kupusini ne dobija.
 *
 * Ovo nije navigacija i ne pokušava da bude: navigaciju vozač otvara po
 * stanici, jednim dodirom. Ovde se vidi celina — koliko je stanica, kojim
 * redom idu i gde se ide dva puta.
 */
export default function TourSketch({ tour }: { tour: DriverTour }) {
  const points = collect(tour);

  if (points.length < 2) {
    return null;
  }

  const path = tour.roadGeometry ?? [];
  const all = [...points.map((p) => [p.lat, p.lon] as [number, number]), ...path];

  const minLat = Math.min(...all.map((p) => p[0]));
  const maxLat = Math.max(...all.map((p) => p[0]));
  const minLon = Math.min(...all.map((p) => p[1]));
  const maxLon = Math.max(...all.map((p) => p[1]));

  // Ivica da čiode i natpisi ne beže van okvira.
  const pad = 26;
  const width = 320;
  const height = 320;

  // Na našoj širini je stepen dužine oko 0,7 stepena širine; bez toga bi
  // Bačka izgledala razvučeno u stranu.
  const lonScale = Math.cos((minLat + maxLat) / 2 * Math.PI / 180);

  const spanLat = Math.max(maxLat - minLat, 0.005);
  const spanLon = Math.max((maxLon - minLon) * lonScale, 0.005);
  const scale = Math.min((width - 2 * pad) / spanLon, (height - 2 * pad) / spanLat);

  const x = (lon: number) => pad + (lon - minLon) * lonScale * scale;
  // Sever je gore: veća širina ide na manji y.
  const y = (lat: number) => height - pad - (lat - minLat) * scale;

  const line = path.length > 1
    ? path.map((p) => `${x(p[1]).toFixed(1)},${y(p[0]).toFixed(1)}`).join(' ')
    : points.map((p) => `${x(p.lon).toFixed(1)},${y(p.lat).toFixed(1)}`).join(' ');

  return (
    <div className="sketch">
      <svg viewBox={`0 0 ${width} ${height}`} role="img" aria-label="Skica ture">
        <polyline
          points={line}
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          strokeOpacity={0.45}
          strokeLinejoin="round"
        />

        {points.map((point) => (
          <g key={point.key}>
            <circle
              cx={x(point.lon)}
              cy={y(point.lat)}
              r={point.kind === 'kraj' ? 7 : 9}
              className={`sketch__pin sketch__pin--${point.kind}`}
            />
            {point.label !== null && (
              <text
                x={x(point.lon)}
                y={y(point.lat) + 4}
                textAnchor="middle"
                className="sketch__no"
              >
                {point.label}
              </text>
            )}
          </g>
        ))}
      </svg>

      <ol className="sketch__legend">
        {points
          .filter((p) => p.label !== null)
          .map((point) => (
            <li key={point.key}>
              <span className={`sketch__dot sketch__pin--${point.kind}`}>{point.label}</span>
              {point.name}
            </li>
          ))}
      </ol>
    </div>
  );
}

interface SketchPoint {
  key: string;
  lat: number;
  lon: number;
  /** Broj stanice; prazno za polazište i povratak. */
  label: number | null;
  name: string;
  kind: 'polaz' | 'kraj' | 'kupac' | 'objekat' | 'uzima';
}

/** Tačke ture redom obilaska, bez onih bez koordinate. */
function collect(tour: DriverTour): SketchPoint[] {
  const points: SketchPoint[] = [];

  if (tour.start.latitude !== null && tour.start.longitude !== null) {
    points.push({
      key: 'polaz',
      lat: tour.start.latitude,
      lon: tour.start.longitude,
      label: null,
      name: tour.start.name,
      kind: 'polaz'
    });
  }

  tour.stops.forEach((stop, index) => {
    if (stop.latitude === null || stop.longitude === null) return;

    points.push({
      key: `s${stop.id}`,
      lat: stop.latitude,
      lon: stop.longitude,
      label: index + 1,
      name: stop.name,
      kind: stop.isPickup ? 'uzima' : stop.kind === 'CustomerAddress' ? 'kupac' : 'objekat'
    });
  });

  if (tour.end.latitude !== null && tour.end.longitude !== null) {
    points.push({
      key: 'kraj',
      lat: tour.end.latitude,
      lon: tour.end.longitude,
      label: null,
      name: tour.end.name,
      kind: 'kraj'
    });
  }

  return points;
}
